// =============================================================================
// VatPricePair — linked excl-VAT / incl-VAT price inputs.
// =============================================================================
// Catalogue prices are stored VAT-INCLUSIVE (gross). This control lets the user
// type EITHER the excl-VAT (net) or the incl-VAT (gross) amount; the other is
// computed live from the VAT rate. The canonical value handed back to the parent
// (`onChangeIncl`) is always the gross/incl-VAT price — the one used everywhere.

import { useEffect, useRef, useState } from 'react';
import { grossToNet, netToGross, round2 } from '../lib/vat';
import { cn } from '../lib/cn';

const show = (n: number): string => (Number.isFinite(n) ? String(round2(n)) : '');

export function VatPricePair({
  rate,
  valueIncl,
  onChangeIncl,
  size = 'md',
  placeholder,
  disabled,
}: {
  /** VAT rate as a fraction, e.g. 0.15. */
  rate: number;
  /** Canonical gross (incl-VAT) price as a string. '' = empty. */
  valueIncl: string;
  onChangeIncl: (v: string) => void;
  size?: 'sm' | 'md';
  placeholder?: string;
  disabled?: boolean;
}): JSX.Element {
  const [excl, setExcl] = useState(() =>
    valueIncl === '' ? '' : show(grossToNet(Number(valueIncl), rate)),
  );
  // Track our own last emit + rate so the resync effect only fires on EXTERNAL
  // changes (parent reset, VAT-rate change) — never while the user is typing,
  // which would clobber a half-typed decimal.
  const lastEmit = useRef(valueIncl);
  const lastRate = useRef(rate);

  useEffect(() => {
    if (valueIncl !== lastEmit.current || rate !== lastRate.current) {
      setExcl(valueIncl === '' ? '' : show(grossToNet(Number(valueIncl), rate)));
      lastEmit.current = valueIncl;
      lastRate.current = rate;
    }
  }, [valueIncl, rate]);

  const emit = (gross: string): void => {
    lastEmit.current = gross;
    onChangeIncl(gross);
  };
  const onExcl = (v: string): void => {
    setExcl(v);
    emit(v === '' ? '' : show(netToGross(Number(v), rate)));
  };
  const onIncl = (v: string): void => {
    setExcl(v === '' ? '' : show(grossToNet(Number(v), rate)));
    emit(v);
  };

  const inputCls = cn('kc-input', size === 'sm' ? 'kc-input-sm w-24 text-right' : 'w-full');
  const wrap = size === 'sm' ? 'flex flex-col gap-1' : 'grid grid-cols-2 gap-2';

  return (
    <div className={wrap}>
      <label className="flex flex-col gap-0.5">
        <span className="text-[9px] uppercase tracking-wide text-ink-4">excl. VAT</span>
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          className={inputCls}
          value={excl}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onExcl(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-[9px] uppercase tracking-wide text-ink-4">incl. VAT</span>
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          className={inputCls}
          value={valueIncl}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onIncl(e.target.value)}
        />
      </label>
    </div>
  );
}
